import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  DashboardAccountNeedingAttentionItem,
  DashboardClientScope
} from 'src/app/models/DashboardMetrics';
import { DashboardMetricsService } from 'src/app/services/dashboard-metrics.service';
import { SnackbarService } from 'src/app/services/snackbar.service';
import { parseDateOnly } from 'src/app/utils/date-only';
import { getErrorMessage } from 'src/app/utils/error-message';

@UntilDestroy()
@Component({
  selector: 'vex-accounts-needing-attention-widget',
  templateUrl: './accounts-needing-attention-widget.component.html',
  styleUrls: ['./accounts-needing-attention-widget.component.scss']
})
export class AccountsNeedingAttentionWidgetComponent implements OnChanges {
  @Input() scope: DashboardClientScope = 'mine';

  loading = false;
  items: DashboardAccountNeedingAttentionItem[] = [];

  constructor(
    private dashboardMetricsService: DashboardMetricsService,
    private snackbarService: SnackbarService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['scope']) {
      this.loadAccounts();
    }
  }

  get emptyMessage(): string {
    return this.scope === 'all'
      ? 'No accounts needing attention.'
      : 'No accounts needing attention for your clients.';
  }

  badgeLabel(item: DashboardAccountNeedingAttentionItem): string {
    if (item.reason === 'PastDue30') {
      return item.daysPastDue != null ? `Past due ${item.daysPastDue}d` : 'Past due';
    }
    if (item.reason === 'LapsedNonPayment') {
      return 'Lapsed for non-payment';
    }
    return 'Lapsed';
  }

  subtitle(item: DashboardAccountNeedingAttentionItem): string {
    if (item.reason === 'PastDue30') {
      return 'Past due over 30 days';
    }

    const ended = this.coverageEndedLabel(item.coverageEndedOn);
    if (item.reason === 'LapsedNonPayment') {
      return ended
        ? `Lapsed for non-payment. Coverage ended ${ended}`
        : 'Lapsed for non-payment.';
    }

    return ended ? `Coverage ended ${ended}` : 'Lapsed';
  }

  actionLabel(item: DashboardAccountNeedingAttentionItem): string {
    return item.reason === 'PastDue30' ? 'Contact' : 'View';
  }

  actionLink(item: DashboardAccountNeedingAttentionItem): string[] {
    const base = ['/clients', item.mCode];
    return item.reason === 'PastDue30' ? [...base, 'contacts'] : base;
  }

  private coverageEndedLabel(value: string | null | undefined): string | null {
    const date = parseDateOnly(value);
    if (!date) {
      return null;
    }
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  }

  private loadAccounts(): void {
    this.loading = true;
    this.dashboardMetricsService.getAccountsNeedingAttention(this.scope)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (result) => {
          this.items = result.items ?? [];
          this.loading = false;
        },
        error: (err) => {
          this.loading = false;
          this.items = [];
          this.snackbarService.open(
            getErrorMessage(err, 'Failed to load accounts needing attention.'),
            'error'
          );
        }
      });
  }
}
